import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Grid as InavistaGrid, GridConfig, GridHyperlinkClickEvent } from '@inavista/components/grid';
import { GridElement } from 'projects/service/src/app/modules/builder/models/grid.model';
import { OrderTypes } from 'projects/service/src/app/shared/models/app.model';
import { ApiResponse, UserRoles } from '../../models/app.model';
import { CommonService } from '../../services/common.service';
import { PageService } from '../../services/page.service';
import { exportInavistaGrid } from '../../utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from '../../utils/local-rows-grid-api';
import { statusPillColor } from '../../utils/status-pill-colors';
import {
    buildDynamicFormGridConfig, dynamicFormGridRows, dynamicFormGridSchema, SYNCFUSION_GRID_WRAPPER_PERSISTENCE_KEY,
} from './syncfusion-grid.grid.config';

// Toolbar + header + horizontal scrollbar: the Syncfusion `height` was the rows area only.
const GRID_CHROME_PX = 92;

// The grid of a form builder "grid" element, on @inavista/components (was a Syncfusion ejs-grid). Its only caller is
// app-dynamic-popup-layout, which gives the element (gridProps); the rows are the element's dataSource, set by the
// form from its fields. The old pageId path (load a Grid page definition, page its API, approve / on-hold exceptions,
// create adjustment orders) had no caller and is gone.
@Component({
    selector: 'app-syncfusion-grid',
    templateUrl: './syncfusion-grid.component.html',
    styleUrls: ['./syncfusion-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    // The grid reads the element's rows, not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class SyncfusionGridComponent implements OnInit {

    @Input() gridProps: GridElement;
    /** The old grid's height (rows area), window height - 330. */
    @Input() gridHeight: number = window.innerHeight - 330;
    @Input() persistenceKey: string = SYNCFUSION_GRID_WRAPPER_PERSISTENCE_KEY;

    @ViewChild('msGrid') msGrid: InavistaGrid;

    gridConfig: GridConfig = null;
    readonly showLoader = signal(false);
    readonly showAdjustmentOrderPopup = signal(false);
    readonly linkedPopupData = signal<{ id: string; type: string; }>(null);
    popupData: any = {};

    private readonly rows = inject(LocalRowsGridApi);
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private pageService: PageService,
        public commonService: CommonService
    ) { }

    ngOnInit(): void {
        if (!this.gridProps) {
            return;
        }
        const role = this.commonService.USER?.role;
        const isManager = [UserRoles.conwayServiceManager, UserRoles.klNationalMGR, UserRoles.klServiceManager].includes(role);
        this.rows.columns.set(dynamicFormGridSchema(this.gridProps));
        this.rows.rows.set(dynamicFormGridRows(this.gridProps, this.gridProps.dataSource));
        this.gridConfig = buildDynamicFormGridConfig(this.gridProps, {
            canSeeTechnician: isManager || role === UserRoles.csa || role === UserRoles.administratorRole,
            canExport: !!this.commonService.roleClaims?.['AllGrid_Toolbar_Excel_Export']?.visible,
            statusColor: statusPillColor(s => this.commonService.getStatusClass(s)),
            persistenceKey: this.persistenceKey || SYNCFUSION_GRID_WRAPPER_PERSISTENCE_KEY,
        });
    }

    get containerHeight(): number {
        return this.gridHeight + GRID_CHROME_PX;
    }

    // A 'ClickableTemplate' column: the shipment order (linked popup) or the adjustment order, as before.
    onHyperlinkClick(event: GridHyperlinkClickEvent) {
        const row = event.row;
        if (event.columnId === 'shipmentOrderId' || event.columnId === 'shipmentOrderLN') {
            this.linkedPopupData.set({ id: row['shipmentOrderId'] as string, type: OrderTypes.shipmentOrder });
        } else if (event.columnId === 'adjustmentOrderNumber') {
            this.openAdjustmentOrderPopup(row['adjustmentOrderNumber'] as string);
        }
    }

    openAdjustmentOrderPopup(orderId: string) {
        this.showLoader.set(true);
        this.pageService.getAdjustmentOrderById(orderId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res: ApiResponse) => {
                if (res.isSuccessful && res.result) {
                    this.popupData = res.result;
                    this.showAdjustmentOrderPopup.set(true);
                } else {
                    this.commonService.showNotification('error', res.message || 'There was a problem getting the record, please try again!');
                }
                this.showLoader.set(false);
            }, (error: HttpErrorResponse) => {
                this.showLoader.set(false);
            });
    }

    // Fixed (Martin, 2026-10-28): the old file name had a stray '}' ("Exceptions}(date).xlsx"); now "Exceptions(date).xlsx".
    onExportRequested() {
        exportInavistaGrid(this.msGrid, this.gridProps.elementLabel);
    }
}
